import { useEffect, type FunctionComponent } from "react";
import { Form, Input, InputNumber, Switch } from "antd";
import { type FormInstance } from "antd/lib/form";
import { useTranslation } from "react-i18next";

import { AI_AUTOMATION_TASK_LIMITS } from "@fitness-app/data-models/entities/AutomationTemplate";

import { ScheduleTimingFields } from "../ScheduleTimingFields/ScheduleTimingFields";
import { TaskSenderField } from "../TaskSenderField/TaskSenderField";
import { TimeField } from "../TimeField/TimeField";
import { type AiAgentMessageTaskFormModel } from "./types";

interface OwnProps {
  formController?: FormInstance<AiAgentMessageTaskFormModel>;
  onSubmit: (formData: AiAgentMessageTaskFormModel) => void;
  // Partial: package-add passes only a scheduleTiming preset, not a full model.
  model?: Partial<AiAgentMessageTaskFormModel> | null;
  // TRE-2072 — the advisor sender only reaches clients of an advisorEnabled automation.
  advisorEnabled?: boolean | null;
}

type Props = OwnProps;

// Same bounds as start-advisor-conversation-task's schema — a task saved here must never
// be rejected there (TRE-2072).
const limits = AI_AUTOMATION_TASK_LIMITS;

const AiAgentMessageTaskForm: FunctionComponent<Props> = ({ formController, onSubmit, model, advisorEnabled }) => {
  const { t } = useTranslation(["automation", "common"]);

  useEffect(() => {
    // reset-then-apply so a preset (or edited task) never leaves stale values
    // from a previous open in the shared form controller.
    formController?.resetFields();
    if (model) {
      formController?.setFieldsValue(model);
    }
  }, [model, formController]);

  return (
    <div>
      <Form<AiAgentMessageTaskFormModel>
        name="ai-agent-message-task-form"
        labelCol={{ span: 8 }}
        wrapperCol={{ span: 14 }}
        labelWrap
        layout="horizontal"
        form={formController}
        initialValues={{
          name: "",
          promptTemplate: "",
          tone: "",
          maxLength: limits.maxLength.default,
          upsellHint: false,
          fallbackMessage: "",
          sender: "trainer",
          sentTimeConfig: { mode: "absolute", avoidSendingOnTheNight: true },
          ...model,
        }}
        onFinish={onSubmit}
      >
        <Form.Item
          name="name"
          label="Nazwa"
          rules={[
            {
              required: true,
              message: t<string>("common:validationErrors.fieldIsRequired"),
            },
          ]}
        >
          <Input placeholder="np. Podsumowanie tygodnia" />
        </Form.Item>

        <Form.Item
          name="promptTemplate"
          label="Intencja wiadomości"
          rules={[
            {
              required: true,
              message: t<string>("common:validationErrors.fieldIsRequired"),
            },
            { min: limits.promptTemplate.min, max: limits.promptTemplate.max },
          ]}
        >
          <Input.TextArea rows={4} placeholder="np. podsumuj tydzień, pochwal konkrety, przypomnij o ankiecie" />
        </Form.Item>

        <Form.Item name="tone" label="Ton wiadomości" rules={[{ max: limits.tone.max }]}>
          <Input placeholder="np. motywująco, konkretnie" maxLength={limits.tone.max} />
        </Form.Item>

        <Form.Item
          name="maxLength"
          label="Maks. długość (znaki)"
          rules={[{ type: "integer", min: limits.maxLength.min, max: limits.maxLength.max }]}
        >
          <InputNumber min={limits.maxLength.min} max={limits.maxLength.max} step={50} precision={0} />
        </Form.Item>

        <Form.Item name="upsellHint" label="Opiekunka może zaproponować ofertę" valuePropName="checked">
          <Switch />
        </Form.Item>

        <Form.Item
          name="fallbackMessage"
          label="Wiadomość zapasowa (gdy generacja się nie uda)"
          extra="Opcjonalna. Bez niej nieudana generacja nie wysyła nic, a zadanie kończy się błędem."
          rules={[{ max: limits.fallbackMessage.max }]}
        >
          <Input.TextArea rows={3} placeholder="Statyczna wiadomość wysyłana, gdy generacja AI się nie powiedzie" />
        </Form.Item>

        <TaskSenderField formController={formController} advisorEnabled={advisorEnabled} />

        <TimeField showOnlyAbsolute={false} label={t<string>("automation:automatedMessageForm.operationTime")} />

        <ScheduleTimingFields formController={formController} />
      </Form>
    </div>
  );
};

export default AiAgentMessageTaskForm;
